import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { ThemeService } from '../../core/services/theme.service';
import { SystemMessages } from '../../core/enums/base.enums';
import { MaintenanceConstants } from '../../core/constants/constants';
import { SystemMessagesMessagingConstants } from '../../core/constants/messaging.constants';
import { UserSessionService } from '../../core/services/user-session.service';
import { AuthorizationStateService } from '../../core/services/authorization/authorization-state.service';
import { MaintenanceService } from '../../core/services/maintenance.service';

const LOGO_LIGHT = "/ms/images/logos/HOAStart-Logo.svg";
const LOGO_DARK = "/ms/images/logos/HOAStart-Logo-dark.svg";

const RETURN_CTA = { buttonText: "Back to Dashboard", buttonUrl: "/dashboard" };

interface SystemPageView {
  statusCode: string;
  // Stands in for the status code on a page that has no error to report.
  icon?: string;
  // Turns the icon slowly, for a page that reports work under way (maintenance) rather than a state.
  spinIcon?: boolean;
  heading: string;
  description: string;
  buttonText: string;
  buttonUrl?: string;
  buttonAction?: () => void;
  showButton: boolean;
}

@Component({
  selector: 'ms-system-page',
  imports: [RouterLink, FontAwesomeModule],
  templateUrl: './system.page.html',
  styleUrl: './system.page.scss'
})
export class SystemPage implements OnInit {

  private activatedRoute = inject(ActivatedRoute);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);
  private userSessionService = inject(UserSessionService);
  private authorizationStateService = inject(AuthorizationStateService);
  private maintenanceService = inject(MaintenanceService);

  private isDarkTheme = toSignal(this.themeService.isDarkThemeActive$, {
    initialValue: this.themeService.isDarkThemeActive()
  });

  public view = signal<SystemPageView>(this.buildGenericView());
  public showStatusCode = computed(() => this.view().statusCode !== "");
  public showIcon = computed(() => this.view().icon !== undefined);
  public logoSrc = computed(() => this.isDarkTheme() ? LOGO_DARK : LOGO_LIGHT);

  public ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const messageId = params.get('messageId') ?? "";
        this.view.set(this.buildView(messageId));

        if (messageId === SystemMessages.Maintenance && this.wasSignOutRequested()) {
          this.signOutAndReturnToSignIn();
          return;
        }

        // Covers a reload landing straight on this page, where the service is a fresh instance that
        // does not yet know it is in maintenance.
        if (messageId === SystemMessages.Maintenance) {
          this.maintenanceService.resumeOnMaintenancePage();
        }
      });
  }

  private buildView(messageId: string): SystemPageView {
    switch (messageId) {

      case SystemMessages.PageNotFound:
        return {
          statusCode: SystemMessages.PageNotFound,
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_PAGE_NOT_FOUND_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_PAGE_NOT_FOUND_DESCRIPTION(),
          ...RETURN_CTA,
          showButton: true
        };

      case SystemMessages.Forbidden:
        return {
          statusCode: SystemMessages.Forbidden,
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_FORBIDDEN_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_FORBIDDEN_DESCRIPTION(),
          ...RETURN_CTA,
          showButton: true
        };

      case SystemMessages.ServerError:
        return {
          statusCode: SystemMessages.ServerError,
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_SERVER_ERROR_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_SERVER_ERROR_DESCRIPTION(),
          ...RETURN_CTA,
          showButton: true
        };

      case SystemMessages.Unauthorized:
        return {
          statusCode: SystemMessages.Unauthorized,
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_UNAUTHORIZED_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_UNAUTHORIZED_DESCRIPTION(),
          ...RETURN_CTA,
          showButton: true
        };

      case SystemMessages.InvitationError:
        return {
          statusCode: "",
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_INVITATION_ERROR_HEADING(),
          description: this.getRouterStateErrorMessage()
            ?? SystemMessagesMessagingConstants.SYSTEM_MESSAGES_INVITATION_ERROR_DESCRIPTION(),
          buttonText: "Return to Sign In",
          buttonUrl: "/sign-in",
          showButton: true
        };

      // No button: the service polls in the background and navigates the user back on its own, and
      // every destination is behind the same outage until it does.
      case SystemMessages.Maintenance:
        return {
          statusCode: "",
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_MAINTENANCE_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_MAINTENANCE_DESCRIPTION(),
          icon: "gear",
          spinIcon: true,
          buttonText: "",
          showButton: false
        };

      case SystemMessages.AuthError:
        return {
          statusCode: "",
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_AUTH_ERROR_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_AUTH_ERROR_DESCRIPTION(),
          buttonText: "Sign Out and Try Again",
          // A plain link to /sign-in would send an already-Auth0-authenticated user
          // straight back through the sign-in -> authenticate -> guard chain that
          // landed them here in the first place. Signing out first guarantees a clean
          // session so the retry has a chance of succeeding instead of bouncing back.
          buttonAction: () => this.signOutAndReturnToSignIn(),
          showButton: true
        };

      // Reached two ways: Auth0 refused the sign-in because the user is blocked, or userLoadedGuard found
      // a still-valid session belonging to an inactive user. Signing out clears either session, so the
      // next attempt shows the login form instead of bouncing straight back here.
      case SystemMessages.AccountDeactivated:
        return {
          statusCode: "",
          heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_ACCOUNT_DEACTIVATED_HEADING(),
          description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_ACCOUNT_DEACTIVATED_DESCRIPTION(),
          icon: "user-lock",
          buttonText: "Return to Sign In",
          buttonAction: () => this.signOutAndReturnToSignIn(),
          showButton: true
        };

      default:
        return this.buildGenericView();
    }
  }

  private buildGenericView(): SystemPageView {
    return {
      statusCode: "",
      heading: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_GENERIC_HEADING(),
      description: SystemMessagesMessagingConstants.SYSTEM_MESSAGES_GENERIC_DESCRIPTION(),
      ...RETURN_CTA,
      showButton: true
    };
  }

  private getRouterStateErrorMessage(): string | undefined {
    const state = window.history.state as { errorMessage?: string } | null;
    return state?.errorMessage;
  }

  // See MaintenanceConstants.SIGN_OUT_QUERY_PARAM: the only way off a maintenance page for someone
  // whose session keeps re-authenticating them.
  private wasSignOutRequested(): boolean {
    const requested = this.activatedRoute.snapshot.queryParamMap.get(MaintenanceConstants.SIGN_OUT_QUERY_PARAM());

    return requested === "true";
  }

  private signOutAndReturnToSignIn(): void {
    try {
      this.authorizationStateService.clearContext();
      this.userSessionService.clearLocalStorage();

      this.authService.logout({
        logoutParams: {
          returnTo: window.location.origin + '/sign-in',
          federated: true
        }
      });
    } catch (error) {
      console.error('SystemPage: Error signing out from auth error state:', error);
    }
  }
}
